import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GoalMetric, MetaProgressoRow } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  metasDoPeriodo, metasProgresso, removerMeta, salvarMeta, unidades,
  vendasPorVendedor,
} from '../lib/consultas';
import { brl, paraNumero, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import { podeAdministrar, useSessao } from '../lib/sessao';

// Tela 13 — Metas trimestrais por unidade. SÓ DO DONO desde 28/09/2026.
//
// Ela nasceu para a equipe inteira ver o progresso do time, e era justamente
// isso que o dono mandou desfazer: "funcionário não pode ver faturamento, nem
// quanto vende" — e meta é faturamento com nome de alvo. Ninguém perdeu dado na
// mudança: não havia nenhuma meta cadastrada no banco.
//
// A rota exige podeVerFaturamento (App.tsx) e o item saiu do menu e da barra de
// quem não é dono (Layout.tsx). Quem nega de verdade é o banco.
//
// O realizado NUNCA é digitado: vem calculado do banco. Meta é alvo; venda
// registrada é progresso — se a venda não está no sistema, não pontua. Isso
// faz a meta puxar a adoção do sistema, de graça.

const METRICAS: Array<{ id: GoalMetric; rotulo: string; formato: 'brl' | 'qtd'; ajuda: string }> = [
  { id: 'faturamento', rotulo: 'Faturamento', formato: 'brl', ajuda: 'Soma das vendas do trimestre (canceladas fora).' },
  { id: 'lucro_liquido', rotulo: 'Lucro líquido', formato: 'brl', ajuda: 'Entradas menos saídas do caixa no trimestre.' },
  { id: 'qtd_vendas', rotulo: 'Vendas', formato: 'qtd', ajuda: 'Quantidade de vendas registradas.' },
  { id: 'ticket_medio', rotulo: 'Ticket médio', formato: 'brl', ajuda: 'Valor médio por venda.' },
];

const rotuloDe = (m: GoalMetric) => METRICAS.find((x) => x.id === m)?.rotulo ?? m;
const formatoDe = (m: GoalMetric, v: number) =>
  METRICAS.find((x) => x.id === m)?.formato === 'qtd' ? qtd(v) : brl(v);

const trimestreDeHoje = () => (Math.floor(new Date().getMonth() / 3) + 1) as 1 | 2 | 3 | 4;

export default function Metas() {
  const { papel, perfil } = useSessao();
  const dono = podeAdministrar(papel);

  const [ano, setAno] = useState(new Date().getFullYear());
  const [trimestre, setTrimestre] = useState<number>(trimestreDeHoje());
  const [editando, setEditando] = useState(false);

  const progresso = useQuery({
    queryKey: ['metas', ano, trimestre],
    queryFn: () => metasProgresso(ano, trimestre),
  });

  // Contribuição pessoal: soma dos meses do trimestre em v_vendas_por_vendedor.
  // Como a tela é do dono, estas são as vendas DELE (ele vende como qualquer
  // um). A RLS recorta do mesmo jeito, por pessoa.
  const contribuicao = useQuery({
    queryKey: ['metas', 'contribuicao', ano, trimestre],
    queryFn: async () => {
      const meses = [0, 1, 2].map((i) => `${ano}-${String((trimestre - 1) * 3 + 1 + i).padStart(2, '0')}-01`);
      const linhas = await Promise.all(meses.map((m) => vendasPorVendedor(m)));
      return linhas.flat().filter((v) => v.vendedor_id === perfil?.id);
    },
    enabled: Boolean(perfil?.id),
  });

  const minhas = useMemo(() => {
    const linhas = contribuicao.data ?? [];
    return {
      total: linhas.reduce((s, v) => s + Number(v.total_vendido), 0),
      vendas: linhas.reduce((s, v) => s + Number(v.vendas), 0),
    };
  }, [contribuicao.data]);

  const metaFaturamento = (progresso.data ?? []).find((m) => m.metrica === 'faturamento');

  return (
    <Layout
      titulo="Metas"
      acao={dono ? (
        <button
          type="button"
          onClick={() => setEditando((v) => !v)}
          className="botao-primario px-3 py-2 text-xs"
        >
          {editando ? 'Fechar' : 'Definir metas'}
        </button>
      ) : null}
    >
      <div className="space-y-4">
        {/* Período */}
        <section className="cartao space-y-3 p-4">
          <div className="grid grid-cols-[7rem_1fr] gap-3">
            <div>
              <label className="rotulo" htmlFor="metas-ano">Ano</label>
              <input
                id="metas-ano" type="number" className="campo" value={ano}
                min={2020} max={2100}
                onChange={(e) => setAno(Number(e.target.value) || new Date().getFullYear())}
              />
            </div>
            <div>
              <p className="rotulo">Trimestre</p>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTrimestre(t)}
                    aria-pressed={trimestre === t}
                    className={[
                      'border px-2 py-3 text-sm font-bold',
                      trimestre === t ? 'border-mute bg-edge text-paper' : 'border-edge text-mute',
                    ].join(' ')}
                  >
                    T{t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-dim">
            T{trimestre} = {['jan–mar', 'abr–jun', 'jul–set', 'out–dez'][trimestre - 1]}.
            O realizado vem das vendas e do caixa registrados no sistema — venda fora do sistema
            não pontua para a meta.
          </p>
        </section>

        {dono && editando && (
          <EditorDeMetas
            ano={ano}
            trimestre={trimestre}
            aoSalvar={() => progresso.refetch()}
          />
        )}

        {progresso.error ? <Aviso tipo="erro">{mensagemDeErro(progresso.error)}</Aviso> : null}
        {progresso.isLoading && <Carregando texto="Calculando progresso" />}

        {/* Progresso das metas */}
        {progresso.data && progresso.data.length > 0 ? (
          <section className="space-y-3">
            {progresso.data.map((m) => <CartaoMeta key={m.goal_id} meta={m} />)}
          </section>
        ) : (
          !progresso.isLoading && (
            <div className="cartao p-6 text-center">
              <p className="font-semibold">Nenhuma meta definida para T{trimestre}/{ano}.</p>
              <p className="mt-2 text-sm text-mute">
                Use "Definir metas" para lançar os alvos do trimestre.
              </p>
            </div>
          )
        )}

        {/* Contribuição pessoal — o que EU vendi dentro do trimestre */}
        {minhas.vendas > 0 && (
          <section className="cartao p-4">
            <h2 className="rotulo">Sua contribuição no trimestre</h2>
            <div className="mt-2 flex items-baseline justify-between gap-3">
              <div>
                <p className="text-2xl font-black tabular-nums">{brl(minhas.total)}</p>
                <p className="text-xs text-dim">{qtd(minhas.vendas)} venda(s) suas</p>
              </div>
              {metaFaturamento && Number(metaFaturamento.valor_meta) > 0 && (
                <p className="text-right text-xs text-mute">
                  {((minhas.total / Number(metaFaturamento.valor_meta)) * 100).toFixed(1)}% da meta
                  de faturamento do time veio de você
                </p>
              )}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}

function CartaoMeta({ meta }: { meta: MetaProgressoRow }) {
  const pct = Number(meta.atingido_pct);
  const batida = pct >= 100;
  // Lucro pode ser negativo: barra trava em [0, 100] mas o número diz a verdade.
  const largura = Math.max(0, Math.min(pct, 100));

  return (
    <article className="cartao p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-bold">
          {rotuloDe(meta.metrica)}
          <span className="ml-2 font-mono text-[0.625rem] uppercase text-dim">{meta.unit_nome}</span>
        </h3>
        <span className={`font-mono text-sm font-bold tabular-nums ${batida ? 'text-ok' : 'text-mute'}`}>
          {pct.toFixed(1)}%
        </span>
      </div>

      <div className="mt-3 h-3 w-full bg-edge" role="progressbar"
           aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}
           aria-label={`${rotuloDe(meta.metrica)}: ${pct.toFixed(1)}% da meta`}>
        <div
          className={`h-3 ${batida ? 'bg-ok' : 'bg-mute'}`}
          style={{ width: `${largura}%` }}
        />
      </div>

      <div className="mt-2 flex items-baseline justify-between text-sm">
        <span className="tabular-nums">
          {formatoDe(meta.metrica, Number(meta.realizado))}
          <span className="text-dim"> de {formatoDe(meta.metrica, Number(meta.valor_meta))}</span>
        </span>
        {!batida && (
          <span className="text-xs text-dim">
            faltam {formatoDe(meta.metrica, Math.max(Number(meta.valor_meta) - Number(meta.realizado), 0))}
          </span>
        )}
        {batida && <span className="text-xs font-bold text-ok">meta batida ✓</span>}
      </div>
    </article>
  );
}

// --- Editor (só dono) --------------------------------------------------------

function EditorDeMetas({
  ano, trimestre, aoSalvar,
}: { ano: number; trimestre: number; aoSalvar: () => void }) {
  const cliente = useQueryClient();

  const lojas = useQuery({ queryKey: ['unidades'], queryFn: unidades, staleTime: 10 * 60_000 });
  const [unitId, setUnitId] = useState('');
  const unidade = unitId || lojas.data?.[0]?.id || '';

  const atuais = useQuery({
    queryKey: ['metas', 'editor', unidade, ano, trimestre],
    queryFn: () => metasDoPeriodo(unidade, ano, trimestre),
    enabled: Boolean(unidade),
  });

  const [valores, setValores] = useState<Record<string, string>>({});

  const valorDe = (m: GoalMetric): string => {
    if (m in valores) return valores[m] ?? '';
    const atual = atuais.data?.find((g) => g.metrica === m);
    return atual ? String(atual.valor_meta) : '';
  };

  const salvar = useMutation({
    mutationFn: async () => {
      for (const m of METRICAS) {
        const texto = valorDe(m.id).trim();
        const atual = atuais.data?.find((g) => g.metrica === m.id);
        const n = paraNumero(texto);

        if (!texto && atual) {
          await removerMeta(atual.id);           // campo esvaziado = meta removida
        } else if (texto) {
          if (n === null || n <= 0) throw new Error(`Valor inválido em "${m.rotulo}".`);
          if (!atual || Number(atual.valor_meta) !== n) {
            await salvarMeta({ unit_id: unidade, ano, trimestre, metrica: m.id, valor_meta: n });
          }
        }
      }
    },
    onSuccess: () => {
      setValores({});
      cliente.invalidateQueries({ queryKey: ['metas'] });
      aoSalvar();
    },
  });

  return (
    <section className="cartao space-y-4 border-signal/50 p-4">
      <h2 className="text-base font-bold">Metas de T{trimestre}/{ano}</h2>

      {(lojas.data?.length ?? 0) > 1 && (
        <div>
          <label className="rotulo" htmlFor="metas-unidade">Unidade</label>
          <select id="metas-unidade" className="campo" value={unidade}
                  onChange={(e) => setUnitId(e.target.value)}>
            {(lojas.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {METRICAS.map((m) => (
          <div key={m.id}>
            <label className="rotulo" htmlFor={`meta-${m.id}`}>
              {m.rotulo} {m.formato === 'brl' ? '(R$)' : '(unidades)'}
            </label>
            <input
              id={`meta-${m.id}`}
              className="campo"
              inputMode="decimal"
              placeholder="sem meta"
              value={valorDe(m.id)}
              onChange={(e) => setValores((v) => ({ ...v, [m.id]: e.target.value }))}
            />
            <p className="mt-1 text-xs text-dim">{m.ajuda}</p>
          </div>
        ))}
      </div>

      <p className="text-xs text-dim">
        Campo vazio = sem meta para a métrica (se existia, é removida). Esta tela é só sua: a
        equipe não vê meta nem progresso.
      </p>

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

      <button type="button" className="botao-primario w-full" disabled={salvar.isPending}
              onClick={() => salvar.mutate()}>
        {salvar.isPending ? 'Salvando…' : 'Salvar metas do trimestre'}
      </button>
    </section>
  );
}
